import MainLayout from 'global/MainLayout';
import useProtectedRoute from 'hooks/useProtectedRoute';
import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import color from '@deprecated-ui/_style/color';
import PageLoader from 'global/Loading/PageLoader';
import { useEffect } from 'react';
import Image from 'global/Image';
import sanitizer from 'string-sanitizer';
import { useRouter } from 'next/router';
import { StateChatList } from 'features/chat/constant';
import groupChatAnalytics from 'features/chat/analytics/GroupChatAnalytics';
import {
  GroupInvitationWrapper,
  InvitationFooterButton,
  LoggedInInvitationWrapper,
  RestMember,
} from './styled';
import groupInvitationStore from './store';
import GroupAvatar from './GroupAvatar';
import MemberAvatar from './MemberAvatar';
import {
  DATA_COMPONENT_GROUP_INVITATION_FOUND_ACTION_BTN,
  DATA_COMPONENT_GROUP_INVITATION_INDEX,
  DATA_COMPONENT_GROUP_INVITATION_NOT_FOUND,
  DATA_COMPONENT_GROUP_INVITATION_NOT_FOUND_IMAGE,
} from './__tests__/constant';

export interface GroupInvitationProps {
  code: string;
}

function GroupInvitation({ code }: GroupInvitationProps) {
  const router = useRouter();

  const { isLoaded } = useProtectedRoute();

  const data = groupInvitationStore((store) => store.data);
  const isLoading = groupInvitationStore((store) => store.isLoading);
  const fetchPreview = groupInvitationStore((store) => store.fetchPreview);
  const joinGroup = groupInvitationStore((store) => store.joinGroup);

  useEffect(() => {
    fetchPreview(code);
  }, []);

  if (!data) {
    return (
      <MainLayout isFlexibleWidth>
        <PageLoader loaded={isLoaded && !isLoading} />
        <LoggedInInvitationWrapper>
          <GroupInvitationWrapper
            data-cy={DATA_COMPONENT_GROUP_INVITATION_NOT_FOUND}
          >
            <Image
              data-cy={DATA_COMPONENT_GROUP_INVITATION_NOT_FOUND_IMAGE}
              src="/illustration/group-invitation-not-found.svg"
              alt="group invitation not found"
            />
            {/* @ts-expect-error */}
            <Text family="bold" size="16px" height="135%" mb="8px">
              Group Not Found
            </Text>
            <Text height="19px" align="center">
              The invitation link you followed is expired or the group has been
              deleted.
            </Text>
          </GroupInvitationWrapper>
        </LoggedInInvitationWrapper>
      </MainLayout>
    );
  }

  const {
    members,
    room: {
      id: roomId,
      info: { avatar_url: avatarUrl, name, shortened_name: shortName },
      state,
    },
    remaining_notation: remaining,
    total_members: totalMembers,
  } = data;

  const showedName = sanitizer.sanitize.keepSpace(name);
  const isInvited = state === StateChatList.ROOM_STATE_INVITED;

  const onClickJoin = () => {
    joinGroup(code);
  };

  /**
   * onClickMessage
   * @param e
   * @param state
   */
  const onClickMessage = (e, s) => {
    e.preventDefault();
    // already joined, redirect to group chat room detail
    if (s === StateChatList.ROOM_STATE_ACCEPTED) {
      // group chat tracker
      groupChatAnalytics.groupInfoSettingsAction.onGroupChatMessageOpened();

      // redirect to group chat room detail
      router.push(`/group-chat/${roomId}`);
    }
  };

  return (
    <MainLayout isFlexibleWidth>
      <PageLoader loaded={isLoaded && !isLoading} />
      <LoggedInInvitationWrapper>
        <GroupInvitationWrapper data-cy={DATA_COMPONENT_GROUP_INVITATION_INDEX}>
          <GroupAvatar
            name={showedName}
            shortName={shortName}
            src={avatarUrl}
          />
          <Text size="16px" family="bold" height="135%">
            {showedName}
          </Text>
          <Text size="11px" color={color.highgray}>
            {totalMembers} Members
          </Text>
          {/* @ts-expect-error */}
          <Flex mt="44px" width="100%" justify="center">
            {members.map((member) => (
              // @ts-expect-error
              <Flex width="62px" p="4px 6px 0 4px" direction="column">
                <MemberAvatar username={member.username} src={member.avatar} />
                <Text
                  size="12px"
                  height="135%"
                  align="center"
                  ellipsis
                  width="46px"
                  // @ts-expect-error
                  mt="6px"
                >
                  {member.username}
                </Text>
              </Flex>
            ))}
            {totalMembers > 4 && (
              <RestMember>
                <i>{remaining}</i>
              </RestMember>
            )}
          </Flex>
          <Flex data-cy={DATA_COMPONENT_GROUP_INVITATION_FOUND_ACTION_BTN}>
            {isInvited ? (
              <InvitationFooterButton onClick={onClickJoin}>
                Join Group
              </InvitationFooterButton>
            ) : (
              <InvitationFooterButton onClick={(e) => onClickMessage(e, state)}>
                Message
              </InvitationFooterButton>
            )}
          </Flex>
        </GroupInvitationWrapper>
      </LoggedInInvitationWrapper>
    </MainLayout>
  );
}

export default GroupInvitation;
